import { readCachedMode, setupMode, writeCachedMode } from "~/utils/onboarding";

// The mode of the dashboard for the current account (utils/onboarding.js, setupMode): "guided" while its integration
// is being set up, "full" once it is done or left, "none" without a plan, null while unknown. Built on the shared
// getting-started state: the navigation, the sidebar and the page read the API once between them. `onboarding`: the
// state a screen already holds (useOnboarding), to avoid a second set of timers.
//
// `leave` opens the full dashboard (the getting started stays reachable from the sidebar), `resume` comes back to the
// guided mode; both are stored for the whole account (`dismissedAt`). `sawGuided`: this browser saw the account guided,
// before or during this visit, which is what makes the full dashboard's welcome worth showing once.
export const useSetupMode = ({ onboarding = null } = {}) => {
  const nuxtApp = useNuxtApp();
  const state = onboarding ?? useOnboarding({ grants: false });
  const accountId = nuxtApp.$account?.id;
  const { trackSetupModeChanged } = useTracking();

  // Read from the browser once per visit and shared: every screen mounted later sees the same starting point.
  const cached = useState(`setup-mode:cached:${ accountId }`, () => readCachedMode(accountId));
  const sawGuided = useState(`setup-mode:saw-guided:${ accountId }`, () => cached.value === "guided");

  const mode = computed(() => setupMode({
    hasSubscription: state.hasSubscription.value,
    loaded: state.loaded.value,
    failed: state.failed.value,
    finished: state.finished.value,
    dismissedAt: state.dismissedAt.value,
    cached: cached.value
  }));
  watch(mode, (value) => {
    if (value === "guided") sawGuided.value = true;
    // Only what the API said is remembered: a mode guessed after a failure is not.
    if (state.loaded.value && !state.failed.value) writeCachedMode(accountId, value);
  }, { immediate: true });

  const toast = useToast();
  const leave = async () => {
    const nextKey = state.next.value?.key ?? null;
    await state.setDismissed(true);
    if (!state.dismissedAt.value) return;
    trackSetupModeChanged({ mode: "full", next_step: nextKey });
    toast.add({
      id: "setup-mode",
      title: nuxtApp.$i18n.t("onboarding.mode.fullTitle"),
      description: nuxtApp.$i18n.t("onboarding.mode.fullText"),
      icon: "i-heroicons-squares-2x2",
      timeout: 6000
    });
  };
  const resume = async () => {
    await state.setDismissed(false);
    if (state.dismissedAt.value) return;
    trackSetupModeChanged({ mode: "guided", next_step: state.next.value?.key ?? null });
    await navigateTo("/dashboard");
  };

  return { ...state, mode, sawGuided, leave, resume };
};
